/**
 * Real tabs per task — the web-mediated trigger (EMBEDDED_BROWSER_V1).
 *
 * The runner decides "this task needs its own tab" but is forbidden from
 * creating tabs on the embedded engine (poison law: Target.createTarget
 * spawns a stray native window there). Tab mutations belong to the SHELL —
 * and the shell doesn't know about task handoffs. This module is the bridge:
 * when a task's browser tool fires, it asks the shell for the tab the task
 * needs (create on handoff, activate on re-entry), and the runner recognizes
 * the fresh target over its own CDP discovery and binds the task to it
 * (ensureTaskSurface in browser-session.ts).
 *
 * This is deliberately the ONE seam that knows how the ask travels. Today it
 * rides Tauri shell commands from the web layer; the planned loopback
 * control channel (runner ⇄ shell over localhost, Phase 2) replaces the
 * transport behind ensureTaskTab without touching its callers.
 *
 * Decision table, per browser tool fire for task X (strip = the merged
 * embeddedTabs: runner truth + shell active marker):
 *   - a tab is stamped taskId===X → the task already owns a tab; on a task
 *     CHANGE bring it forward (activate). Never yanked per tool call — a user
 *     parking on another tab mid-turn stays parked.
 *   - no stamp for X, but another task's stamp exists → real handoff: ask the
 *     shell for a fresh tab on the Vestra start page (never about:blank — the
 *     CEF pane paints it black). Deduped per task so a burst of tool calls
 *     costs one tab.
 *   - no stamps at all (or no tabs yet) → first task on the surface: do
 *     nothing; the runner claims the tab its session is bound to.
 *
 * The runner keeps the navigate-reset as fallback isolation when this
 * trigger never lands (web closed, race lost) — a stale inherited page is
 * the one unacceptable outcome.
 */
import {
  embeddedBrowserActivateTab,
  embeddedBrowserNewTab,
  isEmbeddedLoopbackLive,
} from "./embedded-browser";
import { useBrowserStore } from "@/stores/browserStore";

// One create per task per window — a burst of tool calls (screenshot,
// navigate, click…) must not open a tab each. The strip stamp takes over as
// the dedupe once the runner's claim lands in a BROWSER_TABS snapshot.
const NEW_TAB_DEDUPE_MS = 15_000;

let lastBrowserTaskId: string | null = null;
let lastNewTabTaskId: string | null = null;
let lastNewTabAt = -Infinity;

/** The Vestra new-tab surface, on this deployment's own origin. */
export function embeddedStartPageUrl(win: Window = window): string {
  return `${win.location.origin}/embedded/start`;
}

export function ensureTaskTab(taskId: string, win: Window = window): void {
  // Loopback stand-down: while the shell's control channel is paired, the
  // RUNNER drives tab create/activate directly (browser-session.ts over
  // ctl.req) — a web-side mutation here would race it into a double tab.
  // The strip-click activate and the status wake are untouched; channel
  // down ⇒ this trigger resumes as the fallback vehicle.
  if (isEmbeddedLoopbackLive()) return;
  const tabs = useBrowserStore.getState().embeddedTabs;
  const taskChanged =
    lastBrowserTaskId !== null && lastBrowserTaskId !== taskId;
  lastBrowserTaskId = taskId;

  // Pane not up (or strip not hydrated) — the open path lands a fresh
  // surface and the runner claims it; nothing to mutate.
  if (tabs.length === 0) return;

  const own = tabs.find((t) => t.taskId === taskId);
  if (own) {
    // Re-entry: the task's page is parked exactly where it left it. Bring it
    // forward only when the engaging task CHANGED — same-task tool bursts
    // never fight a user who deliberately clicked another tab.
    if (taskChanged && !own.active)
      embeddedBrowserActivateTab(own.targetId, win);
    return;
  }

  // No stamp for this task. Only a surface some OTHER task owns is a
  // handoff; an unstamped strip means this is the first task — the runner
  // claims the bound tab itself.
  const foreign = tabs.some((t) => t.taskId && t.taskId !== taskId);
  if (!foreign) return;

  const now = Date.now();
  if (lastNewTabTaskId === taskId && now - lastNewTabAt < NEW_TAB_DEDUPE_MS)
    return;
  lastNewTabTaskId = taskId;
  lastNewTabAt = now;
  // The shell creates AND activates the tab, then fires the tabs event; the
  // runner sees Target.targetCreated and binds this task to the new target.
  embeddedBrowserNewTab(embeddedStartPageUrl(win), win);
}

/** Test hook — clear the module's dedupe/handoff memory between cases. */
export function _resetTaskTabsForTests(): void {
  lastBrowserTaskId = null;
  lastNewTabTaskId = null;
  lastNewTabAt = -Infinity;
}
